import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { logger } from '@core/utils';
import config from '@core/config';
import { filesApi } from '../api/filesApi';

/**
 * Custom hook for file upload operations
 * Handles drag-and-drop, multipart upload with progress tracking
 *
 * @param {Object} params
 * @param {string|null} params.userId - User ID for file ownership
 * @param {string} params.accountId - Account ID for file ownership
 * @param {string|null} params.segmentedKnowledgeId - Segmented knowledge ID (optional)
 * @param {string|null} params.folderId - Folder ID to upload files into (optional)
 * @param {Function} params.onSuccess - Callback after successful upload
 * @param {Function} params.onError - Callback on upload error
 *
 * @returns {Object} Upload operations and state
 *
 * @example
 * const { uploadFile, isDragging, uploadProgress, uploadingFiles, dragHandlers } = useFileUpload({
 *   userId: null,
 *   accountId: '123',
 *   segmentedKnowledgeId: null,
 *   onSuccess: () => logger.info('Upload complete'),
 *   onError: (error) => logger.error('Upload failed', error),
 * });
 */
export function useFileUpload({ userId = null, accountId, segmentedKnowledgeId = null, folderId = null, onSuccess, onError }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(new Set());

  const dispatch = useDispatch();

  // Use refs to always have the latest values without recreating handlers
  const folderIdRef = useRef(folderId);
  const uploadFileRef = useRef(null);

  useEffect(() => {
    folderIdRef.current = folderId;
  }, [folderId]);

  /**
   * Upload a single file via multipart POST to /api/v1/files/
   * Uses XHR for upload progress tracking
   * @param {File} file - File object to upload
   */
  const uploadFile = useCallback(async (file) => {
    try {
      setUploadingFiles(prev => new Set([...prev, file.name]));
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

      // Build FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      if (userId) formData.append('user_id', userId);
      if (segmentedKnowledgeId) formData.append('segmented_knowledge_id', segmentedKnowledgeId);
      if (folderIdRef.current) formData.append('folder_id', folderIdRef.current);

      // Upload via XHR for progress tracking
      const token = localStorage.getItem('token');
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        }
      };

      await new Promise((resolve, reject) => {
        xhr.open('POST', `${config.apiBaseUrl}/api/v1/files/`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Token ${token}`);
        }

        xhr.onload = () => {
          if (xhr.status === 201) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
      });

      // Invalidate RTK Query cache to refresh file list
      dispatch(filesApi.util.invalidateTags([{ type: 'Files', id: 'LIST' }]));

      if (onSuccess) {
        onSuccess(file);
      }

    } catch (error) {
      logger.error('Upload error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      // Clean up upload tracking
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.name);
        return newSet;
      });

      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
    }
  }, [userId, segmentedKnowledgeId, dispatch, onSuccess, onError]);

  // Update uploadFile ref whenever it changes
  useEffect(() => {
    uploadFileRef.current = uploadFile;
  }, [uploadFile]);

  /**
   * Handle file input change
   */
  const handleFileInput = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach((file) => uploadFileRef.current(file));
  }, []);

  // Create drag handlers that recreate when folderId changes
  // This ensures PageContainer gets new handlers and reapplies them
  const dragHandlers = useMemo(() => ({
    onDragOver: (e) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => {
      setIsDragging(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      droppedFiles.forEach((file) => uploadFileRef.current(file));
    },
  }), [folderId]); // Recreate when folderId changes

  return {
    uploadFile,
    isDragging,
    uploadProgress,
    uploadingFiles,
    dragHandlers,
    handleFileInput,
  };
}
